import api from './api';
import type { InventoryItem } from '../types/inventory';

export interface DashboardStats {
  total_purchases: number;
  total_sales: number;
  outstanding_amount: number;
  outstanding_count: number;
  average_shrinkage: number;
  shrinkage_trend: 'up' | 'down' | 'stable';
  net_profit: number;
  net_profit_change: number;
  green_bean_value: number;
  finished_goods_value: number;
  roasted_stock_value: number;
}

export interface ActivityItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  status: string;
}

export interface InventoryAlertsData {
  low_stock: InventoryItem[];
  expiring_soon: InventoryItem[];
}

export interface PurchaseOrderExpenseLine {
  id: string;
  expense_type: string;
  item_name: string;
  quantity_loss: number;
  unit_price: number;
  amount: number;
  description: string;
  created_at: string;
}

export interface PurchaseOrderExpenseOverview {
  purchase_order_id: string;
  supplier_id: string;
  po_number: string;
  supplier_name: string;
  order_date?: string;
  status: string;
  payment_status: string;
  delivery_status: string;
  buy_weight: number;
  arrival_weight: number;
  yield_shrinkage: number;
  total_amount: number;
  expense_total: number;
  total_with_expenses: number;
  expense_count: number;
  expenses: PurchaseOrderExpenseLine[];
}

interface DashboardInventoryItemResponse {
  id: string;
  code?: string;
  name: string;
  description?: string;
  category?: string;
  unit_of_measure: string;
  item_type: 'green_bean' | 'roasted_bulk' | 'finished_good';
  reorder_point?: number;
  reorder_quantity?: number;
  standard_cost?: number;
  quantity_on_hand?: number;
  status: 'active' | 'inactive';
  expiry_date?: string;
  estimated_selling_price?: number;
  pack_size?: number;
  green_bean_profile_id?: string;
  work_order_id?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

// Helper to map backend snake_case to frontend camelCase
const mapToInventoryItem = (data: DashboardInventoryItemResponse): InventoryItem => {
  return {
    ...data,
    id: data.id,
    code: data.code,
    name: data.name,
    description: data.description,
    category: data.category,
    unitOfMeasure: data.unit_of_measure,
    itemType: data.item_type,
    reorderPoint: data.reorder_point,
    reorderQuantity: data.reorder_quantity,
    standardCost: data.standard_cost,
    stock: data.quantity_on_hand || 0,
    quantity: data.quantity_on_hand || 0,
    status: data.status,
    expiryDate: data.expiry_date,
    estimatedSellingPrice: data.estimated_selling_price,
    packSizeGrams: data.pack_size,
    greenBeanProfileId: data.green_bean_profile_id,
    workOrderId: data.work_order_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    // Add other fields as needed or rely on partial data for display
  } as InventoryItem;
};

export const dashboardService = {
  getStats: async (startDate?: string, endDate?: string): Promise<DashboardStats> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const response = await api.get<DashboardStats>(`/dashboard/stats?${params.toString()}`);
    return response.data;
  },

  getActivityFeed: async (): Promise<ActivityItem[]> => {
    const response = await api.get<ActivityItem[]>('/dashboard/activity');
    return response.data;
  },

  getInventoryAlerts: async (): Promise<InventoryAlertsData> => {
    const response = await api.get<{ low_stock: DashboardInventoryItemResponse[]; expiring_soon: DashboardInventoryItemResponse[] }>('/dashboard/inventory-alerts');
    return {
      low_stock: (response.data.low_stock || []).map(mapToInventoryItem),
      expiring_soon: (response.data.expiring_soon || []).map(mapToInventoryItem),
    };
  },

  getPurchaseOrderExpenses: async (startDate?: string, endDate?: string): Promise<PurchaseOrderExpenseOverview[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get<PurchaseOrderExpenseOverview[]>(`/dashboard/purchase-order-expenses?${params.toString()}`);
    return response.data;
  },
};
